import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';

import { ThemeSwitch } from '@/components/ThemeSwitch';
import { texts } from '@/lib/texts';

import { useAdmin } from './AuthGuard';
import { DONE_STAGE, NAV_ITEMS } from './router';

const NAV_ID = 'admin-nav';

/**
 * Оболочка админки: сайдбар с разделами 3.8, шапка с пользователем и выходом.
 *
 * Раунд 3, O6: на 1024 и на телефоне постоянный сайдбар в 224 px отнимал половину экрана, и
 * таблицы сжимались в столбик по слову. Ниже `lg` разделы уезжают в выдвижную панель по кнопке
 * «Разделы», основная область занимает всю ширину.
 */
export function Layout() {
  const { me, signOut } = useAdmin();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);

  // Переход по разделу закрывает панель: иначе она остаётся поверх открытой страницы
  useEffect(() => setOpen(false), [location.pathname]);

  // Фокус уходит в открытую панель и возвращается на кнопку «Разделы» после закрытия
  useEffect(() => {
    if (open) closeButton.current?.focus();
    else if (wasOpen.current) menuButton.current?.focus();
    wasOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const role = texts.role[me.role as keyof typeof texts.role] ?? me.role;

  return (
    <div className="min-h-screen lg:flex">
      <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-border bg-surface px-4 py-2.5 lg:hidden">
        <div className="min-w-0">
          <div className="text-base font-bold tracking-tight">{texts.app.short}</div>
        </div>
        <button
          ref={menuButton}
          type="button"
          onClick={() => setOpen(true)}
          aria-expanded={open}
          aria-controls={NAV_ID}
          className="inline-flex items-center gap-2 rounded-lg border border-input px-3 py-1.5 text-sm font-semibold hover:bg-surface-2"
        >
          <span aria-hidden="true" className="text-base leading-none">
            ☰
          </span>
          {texts.nav.open}
        </button>
      </header>

      {open ? (
        <div
          className="fixed inset-0 z-30 bg-overlay lg:hidden"
          aria-hidden="true"
          onClick={() => setOpen(false)}
        />
      ) : null}

      <aside
        id={NAV_ID}
        aria-label={texts.nav.open}
        className={[
          'fixed inset-y-0 left-0 z-40 flex w-64 max-w-[85vw] flex-col overflow-y-auto border-r border-border bg-surface transition-transform',
          'lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-56 lg:max-w-none lg:shrink-0 lg:translate-x-0',
          // Закрытая панель не должна ловить Tab за краем экрана — отсюда `invisible` ниже `lg`
          open ? 'visible translate-x-0 shadow-xl' : 'invisible -translate-x-full lg:visible',
        ].join(' ')}
      >
        <div className="flex items-start justify-between gap-2 px-5 py-5">
          <div>
            <div className="text-lg font-bold tracking-tight">{texts.app.short}</div>
            <div className="text-xs text-muted">{texts.app.title}</div>
          </div>
          <button
            ref={closeButton}
            type="button"
            onClick={() => setOpen(false)}
            aria-label={texts.nav.close}
            className="rounded-md px-2 text-xl leading-none text-muted hover:bg-surface-2 lg:hidden"
          >
            ×
          </button>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 px-3">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                [
                  'flex items-center justify-between rounded-lg px-3 py-2 text-sm font-medium',
                  isActive ? 'bg-accent/15 text-accent-dark' : 'text-fg-soft hover:bg-surface-2',
                ].join(' ')
              }
            >
              <span>{item.label}</span>
              {item.stage > DONE_STAGE ? (
                <span className="text-[10px] text-muted">S{item.stage}</span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pt-4">
          <ThemeSwitch />
        </div>
        <div className="border-t border-border px-5 py-4 text-sm">
          <div className="truncate font-medium">
            {me.firstName} {me.lastName ?? ''}
          </div>
          <div className="text-xs text-muted">
            {me.username ? `@${me.username} · ` : ''}
            {role}
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            className="text-accent-dark mt-2 text-xs font-semibold hover:underline"
          >
            {texts.auth.logout}
          </button>
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
        <Outlet context={useAdmin()} />
      </main>
    </div>
  );
}
